/**
 * Interfaces for common geometric quantities and helpers for common geometric calculations.
 */

interface Position {
  top: number;
  left: number;
}

interface Size {
  width: number;
  height: number;
}

type Rect = Position & Size;

/**
 * Returns the position of the center of the rect.
 */
function computeCenter(rect: Rect): Position {
  return {
    top: rect.top + (rect.height / 2),
    left: rect.left + (rect.width / 2)
  };
}

/**
 * Returns first - second.
 */
function subtractPositions(first: Position, second: Position): Position {
  return {
    top: first.top - second.top,
    left: first.left - second.left
  };
}

/**
 * Returns the max width and height from the two sizes.
 */
function getMaxWidthAndHeight(first: Size, second: Size): Size {
  return {
    width: Math.max(first.width, second.width),
    height: Math.max(first.height, second.height)
  };
}

/**
 * Returns the position that will center the area within the bounds.
 */
function computePositionToCenterAreaInBounds(area: Size, bounds: Rect): Position {
  const boundsCenter = computeCenter(bounds);

  return {
    top: boundsCenter.top - (area.height / 2),
    left: boundsCenter.left - (area.width / 2)
  };
}

/**
 * Returns a position that places the "bounded" region entirely within the "container" region. (i.e. so that
 * the "bounded" region will be bounded by the "container").
 * The returned position will as close as possible to the "bounded" region's current position.
 * If the "bounded" region is too large in a dimension to fit within the "container" region, the returned
 * position will align with the "container" region's top or left boundary.
 */
function computePositionToContainBounds(bounded: Rect, container: Rect): Position {
  const containerBottom = container.top + container.height;
  const containerRight = container.left + container.width;
  return {
    top: Math.max(container.top, Math.min(bounded.top, containerBottom - bounded.height)),
    left: Math.max(container.left, Math.min(bounded.left, containerRight - bounded.width))
  };
}

/**
 * Builds a ClientRect from a position and size.
 */
function makeClientRect(pos: Position, size: Size): ClientRect {
  return {
    top: pos.top,
    left: pos.left,
    bottom: pos.top + size.height,
    right: pos.left + size.width,
    width: size.width,
    height: size.height
  };
}

/**
 * Checks whether a point is contained inside a rectangle.  If the point is on the edges of the rectangle, it is
 * considered inside the rectangle.
 */
function doesRectangleContainPoint(rect: Rect, point: Position): boolean {
  return (point.left >= rect.left && point.left <= (rect.left + rect.width))
    && (point.top >= rect.top && point.top <= (rect.top + rect.height));
}

/**
 * This returns a NEW ClientRect (does not update the one passed in), by adding the passed value to the left edge.
 * The right edge is unchanged.
 */
function addClientRectLeft(rect: ClientRect, difference: number): ClientRect {
  return makeClientRect({left: rect.left + difference, top: rect.top}, {width: rect.width - difference, height: rect.height});
}

/**
 * This returns a NEW ClientRect (does not update the one passed in), by adding the passed value to the right edge.
 * The left edge is unchanged.
 */
function addClientRectRight(rect: ClientRect, difference: number): ClientRect {
  return makeClientRect({left: isNaN(difference) ? NaN : rect.left, top: rect.top}, {width: rect.width + difference, height: rect.height});
}

export {
  Position,
  Size,
  Rect,
  computeCenter,
  subtractPositions,
  getMaxWidthAndHeight,
  computePositionToCenterAreaInBounds,
  computePositionToContainBounds,
  makeClientRect,
  doesRectangleContainPoint,
  addClientRectLeft,
  addClientRectRight
};
